import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { useAuthStore } from '@/stores/auth';
import { useExtensionsStore } from '@/stores/extensions';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { hasPlaceholderPermission, usePermission } from '@/lib/permissions';
import { TasksPage } from '@/features/tasks/components/tasks-page';

type TasksSearchParams = {
  board?: string;
  task?: string;
  view?: 'overview' | 'activity' | 'board' | 'list' | 'backlog' | 'dashboard' | 'all-issues';
};

const VALID_VIEWS = new Set([
  'overview',
  'activity',
  'board',
  'list',
  'backlog',
  'dashboard',
  'all-issues',
]);

export const Route = createFileRoute('/boards')({
  validateSearch: (search: Record<string, unknown>): TasksSearchParams => ({
    board: typeof search.board === 'string' ? search.board : undefined,
    task: typeof search.task === 'string' ? search.task : undefined,
    view: typeof search.view === 'string' && VALID_VIEWS.has(search.view)
      ? (search.view as TasksSearchParams['view'])
      : undefined,
  }),
  beforeLoad: async () => {
    const { isAuthenticated, isInitialized } = useAuthStore.getState();
    if (!isInitialized) return;
    if (!isAuthenticated) throw redirect({ to: '/login' });

    const user = useAuthStore.getState().user;
    if (hasPlaceholderPermission(user)) throw redirect({ to: '/placeholder' });
  },
  component: BoardsRoute,
});

function BoardsRoute() {
  const isEnabled = useExtensionsStore((s) => s.isExtensionEnabled('task-manager'));
  // `boards.view` is what every /boards endpoint requires. The sidebar already
  // hides the entry without it; this covers arrival by bookmark or shared link,
  // so the page says why instead of filling with 403 error states.
  const canView = usePermission('boards.view');
  const { board: boardId, task: taskKey, view } = Route.useSearch();

  return (
    <AuthGuard>
      <AppLayout>
        {isEnabled && !canView ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <h2 className="text-lg font-semibold text-foreground mb-2">You don&rsquo;t have access to Boards</h2>
            <p className="text-sm text-muted-foreground max-w-md">
              Ask an administrator to grant your role the &ldquo;Open Boards&rdquo; permission in
              Settings &rarr; Access Control.
            </p>
          </div>
        ) : isEnabled ? (
          <TasksPage boardId={boardId} taskKey={taskKey} view={view} />
        ) : (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <h2 className="text-lg font-semibold text-foreground mb-2">Boards is not enabled</h2>
            <p className="text-sm text-muted-foreground max-w-md">
              Enable the Task Manager extension in Settings &rarr; Integrations to start using task boards.
            </p>
          </div>
        )}
      </AppLayout>
    </AuthGuard>
  );
}
